import { cn } from '@/lib/utils';
import type { IdentifierStrength } from '@rock/util';
import type { FC, ReactNode } from 'react';

/** A small coloured code tag: a model, a version, a rule's state. Readable on a card and inside a tooltip. */
export const Tag: FC<{ className?: string; children: ReactNode }> = ({ className, children }) => (
    <span
        className={cn(
            'inline-flex items-center rounded bg-slate-200 px-1.5 py-px font-mono text-[10px] font-medium leading-4 text-slate-800',
            className,
        )}
    >
        {children}
    </span>
);

/** A version string or other code name, in the neutral tag. */
export const CodeTag: FC<{ children: ReactNode }> = ({ children }) => <Tag>{children}</Tag>;

/** How much an identifier says on its own — the one predicate recall matching and the catalog join share. */
export const StrengthTag: FC<{ strength: IdentifierStrength }> = ({ strength }) =>
    strength === 'strong' ? (
        <Tag className="bg-blue-200 text-blue-900">strong</Tag>
    ) : (
        <Tag className="bg-rose-200 text-rose-900">weak</Tag>
    );

/** A labelled line in a tooltip: a muted label, then its tags. */
export const TagRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
    <span className="flex flex-wrap items-center gap-1">
        <span className="opacity-70">{label}</span>
        {children}
    </span>
);

/** One line of a tier hint: what a strength means for the gate. */
export const StrengthLine: FC<{ strength: IdentifierStrength; children: ReactNode }> = ({ strength, children }) => (
    <span>
        <StrengthTag strength={strength} /> {children}
    </span>
);
